import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config';

const initialState = {
  calibrationData: [],
  selectedVP: '',
  selectedTagging: '',
  selectedCP: '',
  selectedCalibrationStatus: [],
  selectedPerson: null,
  peopleUnderVp: [],
  loading: false,
  error: null,
  currentIndex: 0,
  editableValues: {},
  midYearReviewData: [],
  midYearReviewLoading: false,
  midYearReviewError: null,
};

// ------------------------- ASYNC THUNKS -------------------------

export const fetchCalibrationData = createAsyncThunk(
  'user/fetchCalibrationData',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(`${config.apiURL}/calibration-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching calibration data:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to fetch calibration data'
      );
    }
  }
);

export const fetchFilteredCalibrationData = createAsyncThunk(
  'user/fetchFilteredCalibrationData',
  async (namesArray, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(`${config.apiURL}/calibration-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Updated filtering logic
      const calibrationData = response.data.filter((item) => {
        // Use INCREMENT_MATRIX_VP if it has a value, otherwise fall back to REVIEW VP.
        const vpToFilterBy = item['INCREMENT_MATRIX_VP'] || item['REVIEW VP'];
        return namesArray.includes(vpToFilterBy);
      });

      return Array.isArray(calibrationData) ? calibrationData : [];
    } catch (error) {
      console.error('Error fetching filtered calibration data:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to fetch filtered calibration data'
      );
    }
  }
);

export const fetchTagFilteredCalibrationData = createAsyncThunk(
  'user/fetchTagFilteredCalibrationData',
  async (tagging, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(`${config.apiURL}/calibration-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const calibrationData = response.data.filter(
        (item) => item['TAGGING'] === tagging // 🔹 dynamic value from frontend
      );

      return Array.isArray(calibrationData) ? calibrationData : [];
    } catch (error) {
      console.error('Error fetching calibration data by tagging:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to fetch calibration data by tagging'
      );
    }
  }
);

export const updateCalibrationData = createAsyncThunk(
  'user/updateCalibrationData',
  async ({ employeeId, data }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.put(
        `${config.apiURL}/calibration-data/${employeeId}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating calibration data:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to update calibration data'
      );
    }
  }
);

// 🔹 CORRECTED: Increment data update async thunk
export const updateIncrementData = createAsyncThunk(
  'user/updateIncrementData',
  async ({ employeeId, updates }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.post(
        `${config.apiURL}/increment-data/${employeeId}`, // employeeId in URL
        { updates }, // only updates in body
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating increment data:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to update increment data'
      );
    }
  }
);
export const fetchMidYearReviewData = createAsyncThunk(
  'user/fetchMidYearReviewData',
  async (employeeId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(
        `${config.apiURL}/mid-year-review/${employeeId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching mid-year review data:', error);
      return rejectWithValue(
        error.response?.data || 'Failed to fetch mid-year review data'
      );
    }
  }
);

// ------------------------- SLICE -------------------------

const PmsUsSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setSelectedVP: (state, action) => {
      state.selectedVP = action.payload;
    },
    setSelectedTagging: (state, action) => {
      state.selectedTagging = action.payload;
    },
    setSelectedCP: (state, action) => {
      state.selectedCP = action.payload;
    },
    setSelectedCalibrationStatus: (state, action) => {
      state.selectedCalibrationStatus = action.payload;
    },
    setSelectedPerson: (state, action) => {
      state.selectedPerson = action.payload;
    },
    setpeopleUnderVp: (state, action) => {
      state.peopleUnderVp = action.payload;
    },
    resetSelections: (state) => {
      state.selectedVP = '';
      state.selectedTagging = '';
      state.selectedCP = '';
      state.selectedCalibrationStatus = [];
      state.selectedPerson = null;
    },
    setCurrentIndex: (state, action) => {
      state.currentIndex = action.payload;
    },
    setEditableValues: (state, action) => {
      state.editableValues = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // --- FETCH CALIBRATION ---
      .addCase(fetchCalibrationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCalibrationData.fulfilled, (state, action) => {
        state.loading = false;
        state.calibrationData = action.payload;
      })
      .addCase(fetchCalibrationData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // --- FILTERED CALIBRATION ---
      .addCase(fetchFilteredCalibrationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFilteredCalibrationData.fulfilled, (state, action) => {
        state.loading = false;
        state.calibrationData = action.payload;
      })
      .addCase(fetchFilteredCalibrationData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // --- TAG FILTER ---
      .addCase(fetchTagFilteredCalibrationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTagFilteredCalibrationData.fulfilled, (state, action) => {
        state.loading = false;
        state.calibrationData = action.payload;
      })
      .addCase(fetchTagFilteredCalibrationData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // --- UPDATE CALIBRATION ---
      .addCase(updateCalibrationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCalibrationData.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally update state.calibrationData with action.payload if needed
      })
      .addCase(updateCalibrationData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      //------------------- MID YEAR REVIEW ------------------
      .addCase(fetchMidYearReviewData.pending, (state) => {
        state.midYearReviewLoading = true;
        state.midYearReviewError = null;
      })
      .addCase(fetchMidYearReviewData.fulfilled, (state, action) => {
        state.midYearReviewLoading = false;
        state.midYearReviewData = action.payload;
      })
      .addCase(fetchMidYearReviewData.rejected, (state, action) => {
        state.midYearReviewLoading = false;
        state.midYearReviewError = action.payload;
      })
      // --- UPDATE INCREMENT ---
      .addCase(updateIncrementData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateIncrementData.fulfilled, (state, action) => {
        state.loading = false;
        const { employeeId, updates } = action.payload;

        // Only update the local state if the update was for a specific user.
        // If 'ALL' was updated, the local state would be out of sync anyway,
        // and a full re-fetch would be a better strategy.
        if (employeeId && employeeId !== 'ALL' && updates) {
          state.calibrationData = state.calibrationData.map((item) =>
            // IMPORTANT: Ensure item['EMP_ID'] is the correct key for the employee ID in your state
            item['EMP_ID'] === employeeId
              ? { ...item, ...updates } // Merge the updates into the specific employee's data
              : item
          );
        }
      })
      .addCase(updateIncrementData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedVP,
  setSelectedTagging,
  setSelectedCP,
  setSelectedCalibrationStatus,
  setSelectedPerson,
  resetSelections,
  setCurrentIndex,
  setpeopleUnderVp,
  setEditableValues,
} = PmsUsSlice.actions;

export default PmsUsSlice.reducer;
